// Campaign Filters Component
import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Tag, 
  MapPin, 
  Activity, 
  ArrowUpDown,
  Building2
} from "lucide-react";

export default function CampaignFilters({ filters, filterOptions, onFilterChange }) {
  if (!filterOptions) return null;

  const handleFilterChange = (key, value) => {
    // Convert "all" back to empty string for the API
    onFilterChange(key, value === "all" ? "" : value);
  };

  const activeFilterCount = [
    filters.brand,
    filters.region,
    filters.status,
    filters.sort_by && filters.sort_by !== "recent"
  ].filter(Boolean).length;

  return (
    <div className="space-y-3">
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          <Badge variant="secondary">
            {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'}
          </Badge>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Brand Filter */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            Brand
          </label>
          <Select
            value={filters.brand || "all"}
            onValueChange={(value) => handleFilterChange("brand", value)}
          >
            <SelectTrigger className="bg-background/60 border-border/60 hover:bg-background transition-colors">
              <SelectValue placeholder="All Brands" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-2">
                  All Brands
                  <Badge variant="secondary" className="ml-auto">
                    {filterOptions.brands?.length || 0}
                  </Badge>
                </span>
              </SelectItem>
              {filterOptions.brands?.map((brand) => (
                <SelectItem key={brand} value={brand}>
                  {brand}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Region Filter */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            Region
          </label>
          <Select
            value={filters.region || "all"}
            onValueChange={(value) => handleFilterChange("region", value)}
          >
            <SelectTrigger className="bg-background/60 border-border/60 hover:bg-background transition-colors">
              <SelectValue placeholder="All Regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-2">
                  All Regions
                  <Badge variant="secondary" className="ml-auto">
                    {filterOptions.regions?.length || 0}
                  </Badge>
                </span>
              </SelectItem>
              {filterOptions.regions?.map((region) => (
                <SelectItem key={region} value={region}>
                  {region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Activity className="h-4 w-4 text-muted-foreground" />
            Status
          </label>
          <Select
            value={filters.status || "all"}
            onValueChange={(value) => handleFilterChange("status", value)}
          >
            <SelectTrigger className="bg-background/60 border-border/60 hover:bg-background transition-colors">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-2">
                  All Statuses
                  <Badge variant="secondary" className="ml-auto">
                    {filterOptions.statuses?.length || 0}
                  </Badge>
                </span>
              </SelectItem>
              {filterOptions.statuses?.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sort By */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium">
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
            Sort By
          </label>
          <Select
            value={filters.sort_by || "recent"}
            onValueChange={(value) => onFilterChange("sort_by", value)}
          >
            <SelectTrigger className="bg-background/60 border-border/60 hover:bg-background transition-colors">
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most Recent</SelectItem>
              <SelectItem value="budget_high">Budget (High to Low)</SelectItem>
              <SelectItem value="budget_low">Budget (Low to High)</SelectItem>
              <SelectItem value="name">Campaign Name</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
